'use client';

import * as React from 'react';

/**
 * Shared state for the AdaptiveMenu family. `VariantContext` decides whether a
 * component renders its desktop dropdown form or its mobile drawer form;
 * `NavContext`/`PanelContext`/`SubContext` drive the mobile drill-down panels.
 */

export type Variant = 'menu' | 'drawer';

export const VariantContext = React.createContext<Variant>('menu');

export type NavContextValue = {
  /** id of the panel currently shown; `null` is the root panel. */
  activePanelId: string | null;
  push: (id: string, onOpenChange?: (open: boolean) => void) => void;
  pop: () => void;
  close: () => void;
};

export const NavContext = React.createContext<NavContextValue | null>(null);

/** Which panel a subtree belongs to. `null` = root panel. */
export const PanelContext = React.createContext<string | null>(null);

export type SubContextValue = {
  subId: string;
  title: React.ReactNode;
  onOpenChange?: (open: boolean) => void;
};

export const SubContext = React.createContext<SubContextValue | null>(null);

/** True when the calling subtree belongs to the panel currently shown. */
export const useIsActivePanel = (): boolean => {
  const nav = React.useContext(NavContext);
  const panelId = React.useContext(PanelContext);
  // In the desktop menu there is no nav stack; everything is "active".
  return !nav || nav.activePanelId === panelId;
};
